import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const COLOURS = {
  futsal: "#E5A91E",
  five: "#E32118",
  seven: "#3FA45B",
  nine: "#4A7FD1",
  eleven_youth: "#9B6BD1",
  eleven_adult: "#D18F3F",
};

/** Enter a plot and see a drawn layout: how many pitches fit, in which orientation, with what left over. */
export const SiteLayoutPlanner = () => {
  const [formats, setFormats] = useState([]);
  const [choice, setChoice] = useState("five");
  const [length, setLength] = useState("120");
  const [width, setWidth] = useState("70");

  useEffect(() => {
    api.get("/pitch-sizing").then((r) => setFormats(r.data.formats)).catch(() => setFormats([]));
  }, []);

  const plot = { l: Math.max(0, parseFloat(length) || 0), w: Math.max(0, parseFloat(width) || 0) };
  const fmt = formats.find((f) => f.id === choice);

  const layout = useMemo(() => {
    if (!fmt || !plot.l || !plot.w) return null;
    const pl = fmt.fenced_length_m;
    const pw = fmt.fenced_width_m;
    const options = [
      { rot: false, cols: Math.floor(plot.w / pw), rows: Math.floor(plot.l / pl), cw: pw, ch: pl },
      { rot: true, cols: Math.floor(plot.w / pl), rows: Math.floor(plot.l / pw), cw: pl, ch: pw },
    ].map((o) => ({ ...o, count: Math.max(0, o.cols) * Math.max(0, o.rows) }));
    const best = options.sort((a, b) => b.count - a.count)[0];
    const capped = Math.min(best.count, 400);
    const used = best.count * fmt.fenced_area_m2;
    return { ...best, capped, used, area: plot.l * plot.w, spare: plot.l * plot.w - used };
  }, [fmt, plot.l, plot.w]);

  const scale = plot.w && plot.l ? Math.min(1000 / plot.w, 520 / plot.l) : 1;
  const input = "mt-2 w-28 border border-white/20 bg-transparent px-3 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";
  const label = "mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500";

  return (
    <section className="mt-20" data-testid="site-layout-planner">
      <p className="eyebrow">Site layout planner</p>
      <h2 className="display-2 mt-4 max-w-3xl">Draw What Fits On Your Plot</h2>
      <p className="body-copy mt-5 max-w-3xl">
        Enter the length and width of the land. The planner tries both orientations, keeps the run-off on every
        side and draws the best fit it can find.
      </p>

      <div className="mt-8 flex flex-wrap items-end gap-5">
        <label className="block">
          <span className={label}>Plot length, m</span>
          <input value={length} onChange={(e) => setLength(e.target.value.replace(/[^\d.]/g, ""))} data-testid="layout-length" className={input} />
        </label>
        <label className="block">
          <span className={label}>Plot width, m</span>
          <input value={width} onChange={(e) => setWidth(e.target.value.replace(/[^\d.]/g, ""))} data-testid="layout-width" className={input} />
        </label>
        <label className="block">
          <span className={label}>Pitch format</span>
          <select value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="layout-format" className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none">
            {formats.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </label>
      </div>

      {layout && fmt ? (
        <>
          <dl className="mono mt-8 grid gap-3 sm:grid-cols-4" data-testid="layout-summary">
            {[
              ["Pitches that fit", layout.count.toLocaleString()],
              ["Orientation", layout.rot ? "Turned sideways" : "Along the plot"],
              ["Plot area", `${Math.round(layout.area).toLocaleString()} m²`],
              ["Left over", `${Math.round(layout.spare).toLocaleString()} m²`],
            ].map(([k, v]) => (
              <div key={k} className="border border-zinc-900 bg-[#0b0b0b] p-4">
                <dt className="text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</dt>
                <dd className="mt-2 font-display text-xl text-white">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-8 overflow-x-auto border border-zinc-900 bg-[#0b0b0b] p-4">
            <svg
              viewBox={`0 0 ${plot.w * scale + 8} ${plot.l * scale + 8}`}
              width={plot.w * scale + 8}
              height={plot.l * scale + 8}
              data-testid="layout-svg"
              className="max-w-full"
            >
              <rect x="4" y="4" width={plot.w * scale} height={plot.l * scale} fill="#050505" stroke="#3f3f46" strokeDasharray="6 4" />
              {Array.from({ length: layout.rows }).flatMap((_, r) =>
                Array.from({ length: layout.cols }).map((_, c) => {
                  if (r * layout.cols + c >= layout.capped) return null;
                  const w = layout.cw * scale;
                  const h = layout.ch * scale;
                  return (
                    <g key={`${r}-${c}`}>
                      <rect x={4 + c * w} y={4 + r * h} width={w} height={h} fill="none" stroke="#27272a" />
                      <rect
                        x={4 + c * w + w * 0.06}
                        y={4 + r * h + h * 0.06}
                        width={w * 0.88}
                        height={h * 0.88}
                        fill={`${COLOURS[fmt.id] || "#E5A91E"}22`}
                        stroke={COLOURS[fmt.id] || "#E5A91E"}
                      />
                      <line
                        x1={4 + c * w + w * 0.06}
                        y1={4 + r * h + h / 2}
                        x2={4 + c * w + w * 0.94}
                        y2={4 + r * h + h / 2}
                        stroke={COLOURS[fmt.id] || "#E5A91E"}
                        strokeOpacity="0.5"
                      />
                    </g>
                  );
                }),
              )}
            </svg>
          </div>
          <p className="mono mt-4 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
            {layout.count > layout.capped ? `First ${layout.capped} of ${layout.count} pitches drawn. ` : ""}
            Each block is {fmt.fenced_length_m} x {fmt.fenced_width_m} m — the {fmt.length} x {fmt.width} m playing
            area plus a {fmt.run_off} m run-off on all four sides. Dashed line is your plot boundary. The planner
            does not know about slope, access, drainage or where the changing rooms go.
          </p>
        </>
      ) : null}
    </section>
  );
};
